/* global fbq */
/**
 *
 *  Lifestyle Elevated page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 21/04/2025.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Title, Meta, Link as LinkMeta } from "react-meta-elements";

import { useAppState } from "../../../state";
import { usePrevState } from "../../../context/PrevStateProvider";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../../Button/Button";
import EventCard from "../EventCard/EventCard";
import BreadcrumbNav from "../../BreadcrumbNav/BreadcrumbNav";
import { CityPickerLS } from "../../CityPickerModal/CityPickerModal";
import { ElevatedClass } from "../../EventPage/ElevatedClass/ElevatedClass";
import HeadlineWithSeparator from "../../HeadlineWithSeparator/HeadlineWithSeparator";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";
import useLatest from "../../../hooks/useLatest/useLatest";

// Utils
import { getTz } from "../../../utils/date";
import { clientWidth, getPartnerPictureUrl } from "../../../utils";
import { modalStyles, PAGE_SIZE } from "../../../constants";
import { groupBy } from "../../../utils/_";

// Icons
import ArrowLeftIcon from "../../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";
import MapMarkerIcon from "../../../icons/MapMarkerIcon";
import KeyIcon from "../../../icons/KeyIcon";

// Images

export default function LifestyleElevatedPage() {
  const { theme } = useTheme();
  const { region } = useRegion();
  const {
    user,
    hasTriedToAutoLogin,
    isLoggedIn,
    elevatedEvents,
    gatedEventsLoaded,
    hasMoreGatedEvents,
    isElevatedEventsFetching,
    gatedEventsPageLoaded,
    loadElevatedEvents,
    clearElevatedEventsLoaded,
  } = useAppState();

  const { prevState } = usePrevState();

  const [isCityPickerModalOpen, setIsCityPickerModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const selectedCityLatest = useLatest(selectedCity);

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const gatedEventsLoadedRef = useRef(gatedEventsLoaded);
  const cityPickerResolveRef = useRef(null);
  const isCityPickerModalOpenRef = useRef(false);
  const isMounting = useRef(true);

  useEffect(() => {
    gatedEventsLoadedRef.current = gatedEventsLoaded;
  }, [gatedEventsLoaded]);

  useEffect(
    () => {
      if (!hasTriedToAutoLogin) {
        return;
      }

      const userCity = user && user.city;
      const city =
        userCity && userCity !== "ALL"
          ? userCity
          : selectedCityLatest.current !== "ALL"
          ? selectedCityLatest.current
          : undefined;

      if (city !== selectedCityLatest.current) {
        setSelectedCity(city);
      }

      // Check if the first record matches the pattern '/events/{id}', where {id} is a numerical value
      if (
        (!gatedEventsLoaded &&
          !(
            prevState && prevState.some((item) => item.match(/^\/events\/\d+$/))
          )) ||
        !gatedEventsLoaded
      ) {
        loadElevatedEvents(
          user && user.id,
          "UPCOMING,STARTED,UPCOMING_FULL",
          "FIRST_TIER,SECOND_TIER,THIRD_TIER,FOURTH_TIER,FIFTH_TIER", // tier
          { sort: "start", page: 0, size: PAGE_SIZE },
          user ? user.region : region /*process.env.REACT_APP_REGION*/,
          region === "US" && city
        );
      }
    },
    [user, hasTriedToAutoLogin, prevState, gatedEventsLoaded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // For logout
  // FIXME If we open Event Details page for all users, here might be a problem
  // Fixed using `isMounting`
  useEffect(() => {
    if (isMounting.current) {
      isMounting.current = false;
      return;
    }

    if (!isLoggedIn && gatedEventsLoadedRef.current) {
      clearElevatedEventsLoaded();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (sliderRef.current && elevatedEvents && elevatedEvents.length) {
      setTimeout(() => {
        // Check if it's present, because we might be on different state already
        sliderRef.current &&
          sliderRef.current.scrollTo({
            left: sliderRef.current.scrollWidth,
            behavior: "smooth",
          });
        // sliderRef.current.scrollLeft = sliderRef.current.scrollWidth;
      }, 1000);
    }
  }, [sliderRef.current, elevatedEvents]);

  const isUserLoggedIn = !!(user && user.id);

  const openCityPickerModal = useCallback(() => {
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
      cityPickerResolveRef.current = resolve;

      setIsCityPickerModalOpen(true);
      isCityPickerModalOpenRef.current = true;

      // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, 'Open', EVENT_LOCATION_PICKER_OPEN);
    });
  }, []);

  // `closeLocationSelectorModal` callback should go before `useBodyScrollLock` hook,
  // because of `setShowAllGyms(false)` call, otherwise closing the modal will block the scroll
  // and `useBodyScrollLock` won't have a chance to "unblock" it

  /*
   * After closing the Location Selector Modal (promise resolved) the booking flow continues.
   * @param bail -- whether to skip the booking flow
   */
  const closeCityPickerModal = useCallback(() => {
    setIsCityPickerModalOpen(false);
    isCityPickerModalOpenRef.current = false;
  }, [selectedCity]);

  const handleCityClick = useCallback(() => {
    setIsCityPickerModalOpen(true);
  }, []);

  const handleLocationClick = useCallback(() => openCityPickerModal(), []);

  const handleCityChange = useCallback(
    (city) => {
      // clearElevatedEventsLoaded();
      loadElevatedEvents(
        user && user.id,
        "UPCOMING,STARTED,UPCOMING_FULL",
        "FIRST_TIER,SECOND_TIER,THIRD_TIER,FOURTH_TIER,FIFTH_TIER", // tier
        { sort: "start", page: 0, size: PAGE_SIZE },
        user ? user.region : /*undefined*/ region,
        region === "US" && city !== "ALL" ? city : /*undefined*/ region
      );
      setSelectedCity(city);
    },
    [user]
  );

  const gatedEvents = groupBy(elevatedEvents, (evt) =>
    moment.utc(evt.start).tz(getTz(region)).format("MMMM YYYY")
  );

  const loadMoreFutureEvents = useCallback(
    (page = 0) => {
      const pageableParams = {
        size: PAGE_SIZE,
        sort: "start",
        page,
      };

      const userCity = user && user.city;
      const city =
        userCity && userCity !== "ALL"
          ? userCity
          : selectedCityLatest.current !== "ALL"
          ? selectedCityLatest.current
          : undefined;

      loadElevatedEvents(
        user && user.id,
        "UPCOMING,STARTED,UPCOMING_FULL", // Not used
        "FIRST_TIER,SECOND_TIER,THIRD_TIER,FOURTH_TIER,FIFTH_TIER", // tier
        pageableParams,
        user ? user.region : region /*process.env.REACT_APP_REGION*/,
        region === "US" && city !== "ALL" ? city : undefined
      );

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [user, region]
  );

  // So that when the user gets back, it'll reload the cards
  const handleEventCardClick = useCallback(() => {
    // It's being cleared on Become a Member page?
    if (!user) {
      // clearElevatedEventsLoaded();
    }
  }, [user]);

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Unique Locations, Unforgettable Fitness Events</Title>
      <Meta
        name="description"
        content="TruBe: Unique Locations, Unforgettable Fitness classes in Pilates, Yoga, HIIT, Cardio, and more. Providing the best coaches and fitness and wellbeing experience."
      />
      <LinkMeta rel="canonical" href="https://trubeapp.com/events/upcoming" />
      <Header
        // behaviour="none"
        showRegionSelect={false}
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/lifestyle"
        heroRef={heroRef}
        onSignUpClick={clearElevatedEventsLoaded}
        onLoginClick={clearElevatedEventsLoaded}
      />
      {/*<ElevatedClass />*/}

      <div className="tbk-bg-grey-light_ tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mb-3 tbk-mt-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>Elevated Classes</BreadcrumbNav>
          </div>
        </div>
      </div>

      <div className="tbk-bg-grey-light_ tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-4 tbk-pt-4 lg:tbk-pt-4">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center">
            <KeyIcon className="tbk-mb-2" />
            {/*<h2 className="tbk-text-title-medium tbk-mb-2 tbk-text-basic-white">
              Elevated Classes
            </h2>*/}

            <h2 className="tbk-text-title-bold lg:tbk-text-title-bold tbk-mb-2 tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Elevated Classes
            </h2>
            <p className="tbk-text-secondary tbk-text-center tbk-text-primary md:tbk-w-108">
              Certain TruBe events are invitation-only, offering a more intimate
              and immersive wellness experience.
            </p>
          </div>

          {region === "US" && false ? (
            <button
              className={`tbk-mx-auto tbk-mt-4 tbk-flex tbk-max-w-full tbk-items-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-grey-inactive ${
                selectedCity ? "tbk-py-0.5" : "tbk-py-1"
              } tbk-px-1 tbk-shadow-trube lg:tbk-max-w-xs`}
              onClick={handleCityClick}
            >
              <MapMarkerIcon className="tbk-shrink-0" />
              <div className="tbk-text-small tbk-mr-1 tbk-whitespace-nowrap tbk-text-blue-dark">
                Location:
              </div>
              <div
                className={`location-link tbk-text-small tbk-block tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap ${
                  selectedCity ? "tbk-text-grey" : "tbk-text-grey"
                }`}
              >
                {adaptCityName(selectedCity)}
              </div>
            </button>
          ) : null}
          {Object.keys(gatedEvents).map((eventHeading) => (
            <div className="tbk-pt-4" key={eventHeading}>
              <HeadlineWithSeparator
                component="h2"
                titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
                className="tbk-mb-4 lg:tbk-mb-5"
                separatorClass={"tbk--mt-2 lg:tbk--mt-2"}
              >
                {eventHeading}
              </HeadlineWithSeparator>

              <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3 ">
                {gatedEvents[eventHeading].map((evt, index) => (
                  <EventCard
                    key={index}
                    href={`/events/${evt.id}`}
                    imgUrl={evt.coverPhoto}
                    title={evt.name}
                    startDate={evt.start}
                    endDate={evt.finish}
                    place={evt.location.name}
                    joined={evt.joined}
                    soldOut={evt.capacity === evt.currentCapacity}
                    status={evt.status}
                    region={region}
                    onClick={handleEventCardClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasMoreGatedEvents && !isElevatedEventsFetching ? (
        <Button
          variant="secondary"
          className="tbk-mt-7_ tbk-mx-auto tbk-mb-2 tbk-mb-7 tbk-w-68 md:tbk-mb-7"
          onClick={() => loadMoreFutureEvents(gatedEventsPageLoaded + 1)}
        >
          Show more
        </Button>
      ) : null}

      <div className="tbk-mb-7 tbk-flex tbk-justify-center">
        <Link to={"/lifestyle"}>
          <Button
            className="tbk-w-40 tbk-text-primary"
            variant="secondary"
            size={"md"}
            startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
          >
            Back
          </Button>
        </Link>
      </div>

      <Footer regionSelect={false} />

      <CityPickerLS
        isOpen={isCityPickerModalOpen}
        city={selectedCity}
        onClose={() => setIsCityPickerModalOpen(false)}
        onCityChange={handleCityChange}
      />
    </>
  );
}

const adaptCityName = (cityName) => {
  return cityName
    ? cityName === "LOS_ANGELES"
      ? "Los Angeles"
      : cityName === "SAN_DIEGO"
      ? "San Diego"
      : "All"
    : "All";
};
